import { ComparePageTemplate, type CompareContent } from './ComparePageTemplate';

const content: CompareContent = {
  competitor: 'HireVue',
  competitorSegment: 'Video Interview Platform (not a full ATS)',
  tagline:
    "HireVue adds video on top of your existing ATS. GoGrabJob Business replaces both — full ATS plus AI interviews in one platform, at a fraction of HireVue's enterprise price tag.",
  intro:
    "HireVue pioneered digital interviewing, but it remains a point solution — an overlay you bolt on top of another ATS. That means two contracts, two integrations to maintain, two support queues, and two billing relationships. GoGrabJob Business was built as a single unified platform: structured ATS workflows and AI-assessed interviews share the same data model, the same pipeline view, and the same role-fit scorecard.",

  tableGroups: [
    {
      groupName: 'Platform scope',
      rows: [
        { feature: 'Full applicant tracking system',      ours: 'yes',  theirs: 'no'       },
        { feature: 'Job posting & career page builder',   ours: 'yes',  theirs: 'no'       },
        { feature: 'Pipeline stages & kanban view',       ours: 'yes',  theirs: 'no'       },
        { feature: 'Offer management',                    ours: 'yes',  theirs: 'no'       },
        { feature: 'Video / async AI interviews',         ours: 'yes',  theirs: 'yes'      },
        { feature: 'Voice-only AI interviews',            ours: 'yes',  theirs: 'no'       },
        { feature: 'Live structured panel interviews',    ours: 'yes',  theirs: 'partial'  },
      ],
    },
    {
      groupName: 'AI capabilities',
      rows: [
        { feature: 'Explainable role-fit score',           ours: 'Gap-level analysis', theirs: 'no'     },
        { feature: 'Rubric-based competency assessment',   ours: 'yes',                theirs: 'partial' },
        { feature: 'AI scoring transparency / auditability', ours: 'yes',              theirs: 'no'      },
        { feature: 'Facial / affect analysis',             ours: 'no',  theirs: 'yes'   },
        { feature: 'Multilingual AI (EN/HI/Hinglish)',     ours: 'yes', theirs: 'partial' },
        { feature: 'Job-specific question generation',     ours: 'yes', theirs: 'partial' },
        { feature: 'Automated ranking & shortlisting',     ours: 'yes', theirs: 'yes'    },
      ],
    },
    {
      groupName: 'Pricing & access',
      rows: [
        { feature: 'Free trial / starter plan',    ours: 'yes',          theirs: 'no'           },
        { feature: 'Transparent public pricing',   ours: 'yes',          theirs: 'no'           },
        { feature: 'Starting price',               ours: 'Free → ₹999',  theirs: '$35,000+/yr'  },
        { feature: 'No separate ATS needed',       ours: 'yes',          theirs: 'no'           },
        { feature: 'India / INR pricing',          ours: 'yes',          theirs: 'no'           },
      ],
    },
    {
      groupName: 'Workflow & automation',
      rows: [
        { feature: 'Email sequences & templates',          ours: 'yes',  theirs: 'no'      },
        { feature: 'Visual workflow automation',           ours: 'yes',  theirs: 'no'      },
        { feature: 'Calendar & self-scheduling',           ours: 'yes',  theirs: 'partial' },
        { feature: 'Agency client mode',                   ours: 'yes',  theirs: 'no'      },
        { feature: 'HRIS push-on-hire',                    ours: 'yes',  theirs: 'partial' },
      ],
    },
    {
      groupName: 'Compliance & security',
      rows: [
        { feature: 'GDPR tooling & DPA',           ours: 'yes',  theirs: 'yes'     },
        { feature: 'Audit trail',                  ours: 'yes',  theirs: 'yes'     },
        { feature: 'SSO (SAML / OIDC)',            ours: 'yes',  theirs: 'yes'     },
        { feature: 'No facial affect scoring',     ours: 'yes',  theirs: 'no'      },
        { feature: 'Illinois BIPA / AI Act safe',  ours: 'yes',  theirs: 'partial' },
      ],
    },
  ],

  metrics: [
    { value: '60%',   label: 'Lower software cost',   sub: 'GoGrabJob vs HireVue + separate ATS' },
    { value: '2×',    label: 'More interviews / week', sub: 'AI runs in parallel, not sequentially' },
    { value: '1',     label: 'Platform to manage',    sub: 'vs HireVue + ATS = 2 contracts' },
  ],

  deepDive: [
    {
      title: "HireVue is a video layer, not a hiring system",
      body: "HireVue requires you to maintain an ATS separately — Greenhouse, Workday, SAP SuccessFactors, or similar. That means integrations that break when either vendor ships updates, candidate data living in two places, and recruiters toggling between two UIs. GoGrabJob Business stores the candidate profile, pipeline stage, AI interview assessment, role-fit score, scorecard notes, email thread, and offer documents in a single record. There is nothing to integrate because there is nothing separate.",
      highlight: "One platform, one data model, one invoice — replaces both your ATS and your interview solution.",
    },
    {
      title: "Explainable AI vs affect and facial scoring",
      body: "HireVue's heritage is digital video interviewing with AI-augmented signals including facial expressions, eye contact, and vocal affect. This approach has been the subject of regulatory scrutiny — most notably in Illinois, where the Artificial Intelligence Video Interview Act introduced consent and notification requirements. The EU AI Act classifies certain affect recognition systems as high-risk. GoGrabJob Business takes a different position: our AI scores answers against job-specific competency rubrics. Every score cites the criteria it evaluated and the evidence from the candidate's response. Scoring is transparent, auditable, and defensible.",
    },
    {
      title: "Enterprise pricing that excludes most of the market",
      body: "HireVue is priced for large enterprises with formal procurement teams. Public reports and analyst estimates place HireVue contracts at $35,000–$100,000+ per year depending on volume. This puts HireVue out of reach for mid-market companies, SMEs, and recruitment agencies — precisely the teams that could benefit most from AI-assisted screening. GoGrabJob Business provides the same AI interview and scoring capability on plans starting at ₹999/month, with a free trial that needs no credit card.",
    },
    {
      title: "Candidate experience: async on your terms",
      body: "GoGrabJob Business sends candidates a branded interview link immediately after they apply. They complete it in their own time — desktop or mobile, any time zone. The AI processes responses in real time, and recruiters see a ranked shortlist within hours of the application deadline. HireVue's interview experience has historically been perceived as impersonal and technically demanding. Our lighter interface and voice-only option for bandwidth-constrained candidates make the experience more inclusive and reduce candidate drop-off.",
    },
  ],

  ourAdvantages: [
    'Full ATS + AI interviews in one platform — no separate contract',
    'Explainable scoring: criteria, weighting, and evidence shown clearly',
    'No facial affect analysis — legally safer across all jurisdictions',
    'Voice-only interview mode for bandwidth-limited or accessibility needs',
    'Transparent pricing starting at ₹999/month — no $35k enterprise commitment',
    'Agency client mode for multi-client recruitment businesses',
    'Multilingual: English, Hindi, and Hinglish interviews out of the box',
  ],

  theirAdvantages: [
    'Longer market presence with larger brand recognition in enterprise',
    'Broader integration with large HRIS systems (Workday, SAP) out of box',
    'Higher-volume enterprise throughput (100k+ interview capacity)',
    'Stronger name for US Fortune 500 compliance procurement processes',
  ],

  faqs: [
    {
      q: "Can GoGrabJob replace both our ATS and HireVue at the same time?",
      a: "Yes — this is the most common migration scenario. Customers move their pipeline from an existing ATS and retire their HireVue contract simultaneously. GoGrabJob Business handles the full workflow from job posting through AI interview, scoring, panel scheduling, and offer. You get a single platform with one login, one support team, and one renewal date.",
    },
    {
      q: "Is GoGrabJob's AI interview legally safe in Europe and the US?",
      a: "GoGrabJob Business does not use facial recognition, emotion detection, or affect analysis. Our AI scores answers against job-specific competency rubrics — the same evaluation a human interviewer would apply from a structured interview guide. This approach is compliant with the EU AI Act's requirements for high-risk AI in employment, Illinois BIPA, and the NYC Local Law 144 auditing requirements.",
    },
    {
      q: "How do candidates experience the AI interview?",
      a: "Candidates receive an email with a branded interview link immediately after applying. They click, read the question, and record a video or voice response on any device — no app download required. The interface is conversational, lightweight, and works on 3G mobile connections. Average completion time is 12–18 minutes for a five-question interview. Completion rates average 78% vs. an industry benchmark of 55% for video interview platforms.",
    },
    {
      q: "How does GoGrabJob score answers differently from HireVue?",
      a: "We score answers against your job-specific competency rubric — the criteria you define during job setup. Each answer is evaluated on evidence of the competency (did the candidate demonstrate the behaviour?), depth of example, and relevance to the role. Scores are shown with the rubric criteria and a quote from the candidate's answer. Nothing is a black box. You can review, override, and annotate any score.",
    },
    {
      q: "What happens to candidates who can't access video?",
      a: "GoGrabJob Business supports voice-only interviews for candidates with low bandwidth, accessibility requirements, or who are simply more comfortable on voice. Recruiters can configure per-role whether video, voice, or either is accepted. All AI scoring capabilities apply equally to voice responses.",
    },
  ],
};

export function VsHireVuePage() {
  return <ComparePageTemplate content={content} />;
}
